import { SelectPartnerDialog } from 'legacy/lut/components/widgets/public/salesTeam/components/dialogs/selectPartnerDialog/SelectPartnerDialog'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import {
  PARTNER_SCOPE_TRANSLATION_KEYS,
  WELCOME_FLOW_TRANSLATION_KEYS,
} from 'partner360/features/welcomeFlow/configs/translations'
import { selectWelcomeFlowPartners } from 'partner360/features/welcomeFlow/model/selectors'
import { PartnerCard } from 'partner360/features/welcomeFlow/ui/parts/partnerCard'
import { RequestEditor } from 'partner360/features/welcomeFlow/ui/parts/requestEditor'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'

import { MyPartnersControls } from './components/myPartnersControls'
import { MyPartnersDisclaimer } from './components/myPartnersDisclaimer'
import { MyPartnersSummary } from './components/myPartnersSummary'
import { useMyPartnersFilters } from './hooks/useMyPartnersFilters'
import { useMyPartnersRequests } from './hooks/useMyPartnersRequests'

import styles from './ManagerCockpitMyPartners.module.scss'

interface IManagerCockpitMyPartnersProps {
  isLoading: boolean
  onPartnersChanged?: () => void
}

/** The manager cockpit's My Partners section: the Welcome Flow's partner list laid out like the
 *  cockpit's other sections. The wizard and the H2 dialog keep `PbmPartnersManagement`. */
export const ManagerCockpitMyPartners = ({
  isLoading,
  onPartnersChanged,
}: IManagerCockpitMyPartnersProps) => {
  const partners = useSelector(selectWelcomeFlowPartners)
  const filters = useMyPartnersFilters(partners)
  const requests = useMyPartnersRequests(onPartnersChanged)
  const { filteredPartners } = filters

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {/* The rail's label, with the same count. */}
        <div className={styles.title}>
          <Translation
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.tabs.myPartners}
            replaceParams={[{ key: '{count}', value: partners.length.toString() }]}
          />
        </div>
        <div className={styles.subtitle}>
          <Translation text={WELCOME_FLOW_TRANSLATION_KEYS.bindingsSummary.prefilledDataLabel} />
        </div>
      </div>

      <MyPartnersDisclaimer />

      <MyPartnersSummary partners={partners} />

      <MyPartnersControls filters={filters} onAddPartner={requests.openSelectPartnerDialog} />

      <div className={styles.listTile}>
        {filteredPartners.length > 0 && (
          <div className={styles.list}>
            {filteredPartners.map((partner) => (
              <PartnerCard
                key={partner.PartnerId}
                partner={partner}
                onCreateRemoveRequest={requests.openRemoveRequest}
                onCreateUpdateRequest={requests.openUpdateRequest}
                onRemoveRequest={requests.removeRequest}
              />
            ))}
          </div>
        )}

        {!isLoading && filteredPartners.length === 0 && (
          <div className={styles.empty}>
            <Translation text={PARTNER_SCOPE_TRANSLATION_KEYS.noPartners} />
          </div>
        )}
      </div>

      <SelectPartnerDialog
        isVisible={requests.isSelectPartnerDialogVisible}
        onClose={requests.closeSelectPartnerDialog}
        onSelect={requests.selectPartner}
      />

      <RequestEditor
        isVisible={requests.isRequestEditorVisible}
        onClose={requests.closeRequestEditor}
        currentHpContactRequest={requests.changeRequest}
        onApply={requests.applyRequest}
        partnerId={requests.changeRequest.PartnerId}
        partnerName={requests.changeRequest.PartnerName}
      />

      <LoaderOverlay isVisible={requests.isApplyingRequest} />
    </div>
  )
}

ManagerCockpitMyPartners.displayName = 'ManagerCockpitMyPartners'
